import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';
import styles from '../styles/AdminPage.module.css';
import AdminStats from './subcomponents/AdminStats';
import AdminFilters from './subcomponents/AdminFilters';
import AdminTable from './subcomponents/AdminTable';

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, approved, pending
  const [actionLoading, setActionLoading] = useState({});
  const [expandedUser, setExpandedUser] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({});
  const [swarmAnalyticsData, setSwarmAnalyticsData] = useState({});
  const [workflowAnalyticsData, setWorkflowAnalyticsData] = useState({});
  const [loadingAnalytics, setLoadingAnalytics] = useState({});
  const [analyticsMonths, setAnalyticsMonths] = useState({}); // userId → [{year,month,label}]
  const [analyticsPeriod, setAnalyticsPeriod] = useState({}); // userId → 'lifetime' | 'YYYY-MM'
  const [sortColumn, setSortColumn] = useState('last_login');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users?page=${currentPage}&limit=${usersPerPage}&sort_column=${sortColumn}&sort_direction=${sortDirection}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);

      // Clear analytics cache to force fresh fetch
      setAnalyticsData({});
      setLoadingAnalytics({});

      // If a user is currently expanded, refetch their analytics
      if (expandedUser) {
        const userId = expandedUser;
        setLoadingAnalytics(prev => ({ ...prev, [userId]: true }));
        try {
          const analyticsResponse = await fetch(
            `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/analytics`,
            {
              credentials: 'include',
            }
          );

          if (analyticsResponse.ok) {
            const analyticsData = await analyticsResponse.json();
            setAnalyticsData(prev => ({ ...prev, [userId]: analyticsData.analytics }));
          }
        } catch (error) {
          console.error('Error refetching analytics:', error);
        } finally {
          setLoadingAnalytics(prev => ({ ...prev, [userId]: false }));
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [expandedUser, currentPage, usersPerPage, sortColumn, sortDirection]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprovalToggle = async (userId, currentStatus) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'approve' }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            user_id: userId,
            approved: !currentStatus,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update approval status');
      }

      toast.success(
        currentStatus ? 'User access revoked' : 'User approved successfully'
      );
      await fetchUsers();
    } catch (error) {
      console.error('Error updating approval:', error);
      toast.error('Failed to update approval status');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleAdminToggle = async (userId, currentStatus) => {
    if (userId === user.id) {
      toast.error('Cannot modify your own admin status');
      return;
    }

    setActionLoading(prev => ({ ...prev, [userId]: 'admin' }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/admin-status`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            user_id: userId,
            is_admin: !currentStatus,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update admin status');
      }

      toast.success('Admin status updated successfully');
      await fetchUsers();
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast.error('Failed to update admin status');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (userId === user.id) {
      toast.error('Cannot delete yourself');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [userId]: 'delete' }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/delete`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            user_id: userId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      toast.success('User deleted successfully');
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleRebuildAnalytics = async (userId, userName) => {
    if (!window.confirm(
      `Rebuild analytics for "${userName}"?\n\n` +
      `This will recalculate all statistics from their chat histories.`
    )) {
      return;
    }

    setActionLoading(prev => ({ ...prev, [userId]: 'rebuild' }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/rebuild-analytics`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to rebuild analytics');
      }

      const data = await response.json();
      toast.success('Analytics rebuilt successfully');

      // Update all analytics with freshly rebuilt lifetime data and reset
      // the period selector so the display matches what was just rebuilt.
      setAnalyticsData(prev => ({ ...prev, [userId]: data.analytics }));
      setAnalyticsPeriod(prev => ({ ...prev, [userId]: 'lifetime' }));
      if (data.swarm_analytics) {
        setSwarmAnalyticsData(prev => ({ ...prev, [userId]: data.swarm_analytics }));
      }
      if (data.workflow_analytics) {
        setWorkflowAnalyticsData(prev => ({ ...prev, [userId]: data.workflow_analytics }));
      }
    } catch (error) {
      console.error('Error rebuilding analytics:', error);
      toast.error('Failed to rebuild analytics');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleRebuildAllAnalytics = async () => {
    if (!window.confirm(
      `Rebuild analytics for ALL users?\n\n` +
      `This will recalculate statistics for ${users.length} users. This may take a while.`
    )) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const user of users) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${user.id}/rebuild-analytics`,
          {
            method: 'POST',
            credentials: 'include',
          }
        );

        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error(`Error rebuilding analytics for user ${user.id}:`, error);
        failCount++;
      }
    }

    setLoading(false);

    if (failCount === 0) {
      toast.success(`Successfully rebuilt analytics for all ${successCount} users`);
    } else {
      toast.success(
        `Rebuilt analytics for ${successCount} users. Failed: ${failCount}`
      );
    }

    // Refresh the user list and clear analytics cache
    await fetchUsers();
  };

  const toggleUserDetails = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      return;
    }

    setExpandedUser(userId);

    // If workflow-analytics failed on a prior expand but ask-analytics
    // loaded correctly, only retry the workflow endpoint — don't re-fetch
    // everything and risk overwriting the user's selected period.
    if (!analyticsData[userId] && !loadingAnalytics[userId]) {
      // First expand: fetch all four endpoints together.
      setLoadingAnalytics(prev => ({ ...prev, [userId]: true }));
      try {
        const [analyticsResponse, monthsResponse, swarmResponse, workflowResponse] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/analytics`,
            { credentials: 'include' }
          ),
          fetch(
            `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/analytics/months`,
            { credentials: 'include' }
          ),
          fetch(
            `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/swarm-analytics`,
            { credentials: 'include' }
          ),
          fetch(
            `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/workflow-analytics`,
            { credentials: 'include' }
          ),
        ]);

        if (!analyticsResponse.ok) throw new Error('Failed to fetch analytics');
        const data = await analyticsResponse.json();
        setAnalyticsData(prev => ({ ...prev, [userId]: data.analytics }));

        if (monthsResponse.ok) {
          const mData = await monthsResponse.json();
          setAnalyticsMonths(prev => ({ ...prev, [userId]: mData.months || [] }));
        }
        if (swarmResponse.ok) {
          const sData = await swarmResponse.json();
          setSwarmAnalyticsData(prev => ({ ...prev, [userId]: sData.swarm_analytics }));
        }
        if (workflowResponse.ok) {
          const wData = await workflowResponse.json();
          setWorkflowAnalyticsData(prev => ({ ...prev, [userId]: wData.workflow_analytics }));
        }
        setAnalyticsPeriod(prev => ({ ...prev, [userId]: 'lifetime' }));
      } catch (error) {
        console.error('Error fetching analytics:', error);
        toast.error('Failed to load user analytics');
      } finally {
        setLoadingAnalytics(prev => ({ ...prev, [userId]: false }));
      }
    } else if (!workflowAnalyticsData[userId] && !loadingAnalytics[userId]) {
      // Workflow-only retry: ask-analytics already loaded; only re-fetch
      // the workflow endpoint to avoid overwriting a selected period.
      setLoadingAnalytics(prev => ({ ...prev, [userId]: true }));
      try {
        const wRes = await fetch(
          `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/workflow-analytics`,
          { credentials: 'include' }
        );
        if (wRes.ok) {
          const wData = await wRes.json();
          setWorkflowAnalyticsData(prev => ({ ...prev, [userId]: wData.workflow_analytics }));
        }
      } catch (error) {
        console.error('Error fetching workflow analytics:', error);
      } finally {
        setLoadingAnalytics(prev => ({ ...prev, [userId]: false }));
      }
    }
  };

  const handlePeriodChange = async (userId, period) => {
    setAnalyticsPeriod(prev => ({ ...prev, [userId]: period }));
    setLoadingAnalytics(prev => ({ ...prev, [userId]: true }));
    try {
      const url = period === 'lifetime'
        ? `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/analytics`
        : `${process.env.REACT_APP_AUTH_BACKEND_URL}/api/admin/users/${userId}/analytics?period=${period}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch analytics');
      const data = await response.json();
      setAnalyticsData(prev => ({ ...prev, [userId]: data.analytics }));
    } catch (error) {
      console.error('Error switching period:', error);
      toast.error('Failed to load analytics for selected period');
    } finally {
      setLoadingAnalytics(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleSort = (column) => {
    setCurrentPage(1);
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.company_affiliate?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === 'all' ||
      (filterStatus === 'approved' && u.approved) ||
      (filterStatus === 'pending' && !u.approved);

    return matchesSearch && matchesFilter;
  });

  // Sorting is handled server-side; filteredUsers preserves server order
  const sortedUsers = filteredUsers;

  const stats = {
    total: totalUsers, // Use totalUsers from backend
    approved: users.filter(u => u.approved).length, // This is only for current page, but good enough for now
    pending: users.filter(u => !u.approved).length,
    admins: users.filter(u => u.is_admin).length,
  };

  // Pagination logic
  const totalPages = Math.ceil(totalUsers / usersPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (loading) {
    return (
      <div className={styles.adminPage}>
        <div className={styles.loading}>Loading users...</div>
      </div>
    );
  }

  return (
    <div className={styles.adminPage}>
      <div className={styles.header}>
        <h1>User Management</h1>
        <div className={styles.headerButtons}>
          <button
            onClick={fetchUsers}
            className={styles.refreshButton}
            disabled={loading}
          >
            {loading ? 'Loading...' : '🔄 Refresh Users'}
          </button>
          <button
            onClick={handleRebuildAllAnalytics}
            className={styles.rebuildAllButton}
            disabled={loading}
            title="Rebuild analytics for all users"
          >
            {loading ? 'Rebuilding...' : '🔨 Rebuild All'}
          </button>
        </div>
      </div>

      <AdminStats stats={stats} />

      <AdminFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
      />

      <AdminTable
        sortedUsers={sortedUsers}
        user={user}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        handleSort={handleSort}
        toggleUserDetails={toggleUserDetails}
        expandedUser={expandedUser}
        handleApprovalToggle={handleApprovalToggle}
        handleAdminToggle={handleAdminToggle}
        handleDeleteUser={handleDeleteUser}
        actionLoading={actionLoading}
        loadingAnalytics={loadingAnalytics}
        analyticsData={analyticsData}
        analyticsPeriod={analyticsPeriod}
        analyticsMonths={analyticsMonths}
        handlePeriodChange={handlePeriodChange}
        handleRebuildAnalytics={handleRebuildAnalytics}
        swarmAnalyticsData={swarmAnalyticsData}
        workflowAnalyticsData={workflowAnalyticsData}
      />
      
      {/* Pagination Controls */}
      <div className={styles.pagination}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.paginationButton}
        >
          Previous
        </button>
        <span className={styles.paginationInfo}>
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          className={styles.paginationButton}
        >
          Next
        </button>
      </div>
    </div>
  );
}
